import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Wrench, Plus, Pencil, Trash2, Search } from 'lucide-react';
import { useApp } from '@/store';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { SparePartType, type SparePart } from '@/data/types';
import { useT } from '@/lib/i18n';
import { useConfirm } from '@/components/ConfirmDialog';
import { fmtCurrency } from '@/lib/utils';
import {
  grossToNetInput,
  netToGrossInput,
  priceWithVat,
} from '@/lib/spare-parts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

type Form = {
  articleNr: string;
  name: string;
  type: SparePartType;
  /** Net (excl. VAT) price — the only value ever persisted (client #3). */
  price: string;
  /** Mirror of `price` incl. VAT. Derived on every keystroke, never saved. */
  priceGross: string;
  mfr: string;
  models: string[];
};
const blank = (vatPercent: number): Form => ({
  articleNr: '',
  name: '',
  type: SparePartType.EXTINGUISHER,
  price: '0',
  priceGross: netToGrossInput('0', vatPercent),
  mfr: '',
  models: [],
});
const fromPart = (p: SparePart, vatPercent: number): Form => ({
  articleNr: p.articleNr,
  name: p.name,
  // Pre-fix docs may hydrate without a type — default so a save always
  // persists a valid partType.
  type: p.type ?? SparePartType.EXTINGUISHER,
  price: String(p.price),
  priceGross: netToGrossInput(String(p.price), vatPercent),
  mfr: p.mfr ?? '',
  models: p.models ?? [],
});

const NO_MFR = '__none'; // enum-ignore: sentinel for "no manufacturer" select option

export function SpareParts() {
  const t = useT();
  const confirm = useConfirm();
  const parts = useApp(s => s.spareParts);
  const manufacturers = useApp(s => s.manufacturers);
  const settings = useApp(s => s.settings);
  const upsertSparePart = useApp(s => s.upsertSparePart);
  const renameSparePart = useApp(s => s.renameSparePart);
  const deleteSparePart = useApp(s => s.deleteSparePart);
  const toast = useApp(s => s.toast);
  const can = useSectionRights()[SectionId.SPARE_PARTS];

  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'all' | 'extinguisher' | 'hose'>('all');
  const [mfrFilter, setMfrFilter] = useState('all');
  const [showVat, setShowVat] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SparePart | null>(null);
  const vatPercent = settings.vatPercent;
  const [form, setForm] = useState<Form>(() => blank(vatPercent));
  const [modelSearch, setModelSearch] = useState('');

  // Flat, alphabetically-sorted list of every model across manufacturers,
  // labelled "Manufacturer — Model". Compatibility is stored as model ids.
  const modelOptions = useMemo(
    () =>
      manufacturers
        .flatMap(m =>
          m.models.map(md => ({ id: md.id, label: `${m.name} — ${md.name}` }))
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [manufacturers]
  );
  const filteredModelOptions = useMemo(() => {
    const s = modelSearch.toLowerCase();
    return s
      ? modelOptions.filter(o => o.label.toLowerCase().includes(s))
      : modelOptions;
  }, [modelOptions, modelSearch]);

  const filtered = parts.filter(p => {
    if (tab !== 'all' && p.type !== tab) return false;
    if (mfrFilter !== 'all' && (p.mfr ?? '') !== mfrFilter) return false;
    if (
      q &&
      !p.name.toLowerCase().includes(q.toLowerCase()) &&
      !p.articleNr.includes(q)
    )
      return false;
    return true;
  });

  const displayPrice = (price: number) =>
    fmtCurrency(
      showVat ? priceWithVat(price, vatPercent) : price,
      settings.currency
    );

  // Client #3: excl./incl. VAT sit side by side and each recalculates the
  // other. Only the field the user is NOT typing in gets rewritten — echoing
  // a rounded value back into the focused input makes the two fight over the
  // caret ("1.005" would snap to "1.00" mid-word).
  const setNetPrice = (value: string) =>
    setForm(f => ({
      ...f,
      price: value,
      priceGross: netToGrossInput(value, vatPercent),
    }));
  const setGrossPrice = (value: string) =>
    setForm(f => ({
      ...f,
      priceGross: value,
      price: grossToNetInput(value, vatPercent),
    }));

  const openCreate = () => {
    setEditing(null);
    setForm(blank(vatPercent));
    setModelSearch('');
    setOpen(true);
  };
  const openEdit = (p: SparePart) => {
    setEditing(p);
    setForm(fromPart(p, vatPercent));
    setModelSearch('');
    setOpen(true);
  };
  const toggleModel = (id: string) =>
    setForm(f => ({
      ...f,
      models: f.models.includes(id)
        ? f.models.filter(x => x !== id)
        : [...f.models, id],
    }));
  const handleSave = () => {
    if (!form.articleNr.trim() || !form.name.trim()) return;
    const next: SparePart = {
      articleNr: form.articleNr.trim(),
      name: form.name.trim(),
      type: form.type,
      price: parseFloat(form.price) || 0,
      mfr: form.mfr || undefined,
      // hose parts don't map to extinguisher models — drop any stale mapping
      models: form.type === SparePartType.EXTINGUISHER ? form.models : [],
    };
    if (editing) renameSparePart(editing.articleNr, next);
    else upsertSparePart(next);
    toast(editing ? t('common.save') : t('parts.newPart'));
    setOpen(false);
  };
  const handleDelete = async (p: SparePart) => {
    if (!(await confirm(`${t('common.delete')} ${p.name}?`))) return;
    deleteSparePart(p.articleNr);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Wrench className="h-6 w-6 text-primary" /> {t('parts.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('parts.subtitle')}</p>
        </div>
        {can.create && (
          <Button size="sm" onClick={openCreate} data-focus="parts-new">
            <Plus className="h-4 w-4" />
            {t('parts.newPart')}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <Tabs value={tab} onValueChange={v => setTab(v as typeof tab)}>
              <TabsList>
                <TabsTrigger value="all">
                  {t('common.all')} ({parts.length})
                </TabsTrigger>
                <TabsTrigger value="extinguisher">
                  {t('parts.extinguisher')}
                </TabsTrigger>
                <TabsTrigger value="hose">{t('parts.hose')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {showVat ? t('parts.priceWithVat') : t('parts.priceExclVat')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showVat}
                  aria-label={t('parts.priceWithVat')}
                  onClick={() => setShowVat(v => !v)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                    showVat ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-lg ring-0 transition-transform ${
                      showVat ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </label>
              <Select value={mfrFilter} onValueChange={setMfrFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder={t('ext.manufacturer')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {manufacturers.map(m => (
                    <SelectItem key={m.id} value={m.name}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder={t('parts.searchPlaceholder')}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.articleNr')}</TableHead>
                <TableHead>{t('table.name')}</TableHead>
                <TableHead className="hidden sm:table-cell">
                  {t('ext.manufacturer')}
                </TableHead>
                <TableHead>{t('table.type')}</TableHead>
                <TableHead className="text-right">{t('table.price')}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow key={p.articleNr}>
                  <TableCell className="font-mono text-xs">
                    {p.articleNr}
                  </TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">
                    {p.mfr ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {p.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {displayPrice(p.price)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {can.edit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          title={t('common.edit')}
                          aria-label={t('common.edit')}
                          onClick={() => openEdit(p)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                      {can.delete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 sm:h-7 sm:w-7"
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                          onClick={() => handleDelete(p)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('common.edit') : t('parts.newPart')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <div>
              <Label>{t('table.articleNr')}</Label>
              <Input
                value={form.articleNr}
                onChange={e =>
                  setForm(f => ({ ...f, articleNr: e.target.value }))
                }
                className="font-mono"
              />
              {editing && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('parts.articleNrRenameHint')}
                </p>
              )}
            </div>
            <div>
              <Label>{t('table.type')}</Label>
              <Select
                value={form.type}
                onValueChange={v =>
                  setForm(f => ({ ...f, type: v as SparePartType }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SparePartType.EXTINGUISHER}>
                    {t('parts.extinguisher')}
                  </SelectItem>
                  <SelectItem value={SparePartType.HOSE}>
                    {t('parts.hose')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>{t('table.name')}</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            {/* Client #3 — paired VAT inputs; net is stored, gross is derived */}
            <div className="sm:col-span-2 grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div>
                <Label htmlFor="part-price-net">{t('price.exclVat')}</Label>
                <Input
                  id="part-price-net"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={e => setNetPrice(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="part-price-gross">{t('price.inclVat')}</Label>
                <Input
                  id="part-price-gross"
                  type="number"
                  step="0.01"
                  value={form.priceGross}
                  onChange={e => setGrossPrice(e.target.value)}
                />
              </div>
              <p className="sm:col-span-2 text-xs text-muted-foreground">
                {t('price.vatPairHint')}: {vatPercent}%
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>{t('ext.manufacturer')}</Label>
              <Select
                value={form.mfr || NO_MFR}
                onValueChange={v =>
                  setForm(f => ({ ...f, mfr: v === NO_MFR ? '' : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('ext.manufacturer')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_MFR}>—</SelectItem>
                  {manufacturers.map(m => (
                    <SelectItem key={m.id} value={m.name}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.type === SparePartType.EXTINGUISHER && (
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label>{t('parts.compatibleModels')}</Label>
                  <Badge variant="secondary">
                    {form.models.length} {t('parts.compatibleCount')}
                  </Badge>
                </div>
                <p className="mb-2 text-xs text-muted-foreground">
                  {t('parts.compatibleModelsHint')}
                </p>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={modelSearch}
                    onChange={e => setModelSearch(e.target.value)}
                    placeholder={t('parts.searchModels')}
                    className="pl-8"
                  />
                </div>
                <div className="max-h-48 space-y-1 overflow-auto rounded-md border p-2">
                  {filteredModelOptions.map(o => (
                    <label
                      key={o.id}
                      className="flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-accent"
                    >
                      <Checkbox
                        checked={form.models.includes(o.id)}
                        onCheckedChange={() => toggleModel(o.id)}
                      />
                      {o.label}
                    </label>
                  ))}
                  {filteredModelOptions.length === 0 && (
                    <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                      {t('common.notFound')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
